import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublicPetitionSaveStep4 } from '../../types/publicPetitionApi';
import type { PublicPetitionWizardDraft } from '../../types/publicPetitionCaseRequest';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { CriminalReviewDocumentsTable } from '../wizard/criminal/CriminalReviewDocumentsTable';
import { usePublicPetitionLookups } from '../wizard/publicPetition/usePublicPetitionLookups';
import { apiOrLookupLabel, deliveryMethodDisplay, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: PublicPetitionWizardDraft;
  submittedAt: string;
  step4?: PublicPetitionSaveStep4;
  hideUploadDocumentsStep?: boolean;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

function caseMatterLabel(v: string): string {
  if (v === 'CRIMINAL') return 'Criminal Matter';
  if (v === 'CIVIL') return 'Civil Matter';
  return '—';
}

export default function ViewRequestPublicPetitionCaseDetailsTab({
  caseRequestId,
  draft,
  submittedAt,
  step4,
  hideUploadDocumentsStep = false,
}: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = usePublicPetitionLookups('review');

  const deliveryLabel = useMemo(
    () =>
      deliveryMethodDisplay(
        draft as unknown as CriminalWizardDraft,
        lookups.deliveryMethods,
        step4 as CriminalSaveStep4 | undefined,
        noData,
      ),
    [draft, lookups.deliveryMethods, step4, noData],
  );

  const connectedLabel = yesNoLabel(draft.hasConnectedMatters);

  const submitter = useMemo(() => submitterFromApiStep(step4), [step4]);

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Intake Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Case Matter" value={caseMatterLabel(draft.caseMatter)} />
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={connectedLabel} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                {draft.connectedAgdFileRefAvailable ? (
                  <ConnectedAgdFileRefReviewField
                    caseRequestId={caseRequestId}
                    label="Connected AGD File Reference Number"
                    fileRef={draft.connectedAgdFileRef}
                  />
                ) : (
                  <ReviewField label="Connected AGD File Reference Number" value="Not available" />
                )}
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Parties Involved">
        <ReviewSubBlock title="Petitioner Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Name" value={draft.petitionerName} />
            <ReviewField label="NIC / Identification Number" value={draft.nicOrIdentificationNumber} />
            <ReviewField label="Contact Number" value={draft.petitionerContactNumber} />
            <ReviewField label="Email" value={draft.petitionerEmail} />
            <ReviewField label="Address" value={draft.petitionerAddress} fullWidth />
            <ReviewField label="Represented by Attorney" value={draft.representedByAttorney ? 'Yes' : 'No'} />
            {draft.representedByAttorney ? (
              <>
                <ReviewField label="Attorney Name" value={draft.attorneyName} />
                <ReviewField label="Attorney's Contact Number" value={draft.attorneyContactNumber} />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Respondent Details">
          <ReviewField label="Respondent Name(s)" value={draft.respondentNames} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField
              label="Respondent Type"
              value={apiOrLookupLabel(draft.respondentTypeName, draft.respondentTypeId, lookups.respondentTypes)}
            />
            <ReviewField label="Number of Respondents" value={draft.numberOfRespondents} />
            <ReviewField label="Respondent Address" value={draft.respondentAddress} fullWidth />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Timeline">
        <ReviewSubBlock title="Timeline">
          <ReviewGrid cols={3}>
            <ReviewField
              label="Urgency Level"
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label="Deadline (if any)" value={formatDisplayDate(draft.deadline)} />
            <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      {!hideUploadDocumentsStep ? (
        <ReviewSection step="Step 05" title="Upload Documents">
          <ReviewSubBlock>
            <CriminalReviewDocumentsTable documents={draft.documents} />
          </ReviewSubBlock>
        </ReviewSection>
      ) : null}
    </div>
  );
}
